<template>
  <div class="audit-report-page">
    <van-nav-bar
      title="Audit Report"
      class="detail-nav-bar"
      left-text="Back"
      left-arrow
      @click-left="handleGoBack"
    />

    <div class="date-search-container">
      <div class="date-input" @click="showCalendar = true">
        <van-icon name="calendar-o" class="date-icon" />
        <span class="date-text">{{ dateRangeText || 'Select date range' }}</span>
      </div>
      <van-calendar
        v-model="showCalendar"
        type="range"
        :min-date="minDate"
        :max-date="maxDate"
        :default-date="defaultDateRange"
        @confirm="onDateConfirm"
        @close="showCalendar = false"
        class="calendar-popup"
        :show-confirm="false"
        :formatter="calendarFormatter"
      />
      <van-button type="primary" class="search-btn" @click="handleSearch" size="small">
        Search
      </van-button>
    </div>

    <div class="stats-container">
      <div class="stat-item">
        <div class="stat-value">{{ auditData.totalReport }}</div>
        <div class="stat-label">Total Report</div>
      </div>
      <div class="stat-item">
        <div class="stat-value">{{ auditData.totalHc }}</div>
        <div class="stat-label">Total HC</div>
      </div>
      <div class="stat-item">
        <div class="stat-value">{{ auditData.taskCompletionRate }}%</div>
        <div class="stat-label">Task Completion Rate</div>
      </div>
    </div>

    <div class="section-title">Details</div>

    <van-pull-refresh v-model="refreshing" @refresh="onRefresh">
      <van-list
        v-model="loading"
        :finished="finished"
        finished-text="No more data"
        @load="onLoad"
        :immediate-check="false"
      >
        <div
          v-for="(item, index) in auditData.list"
          :key="index"
          class="detail-card"
          @click="goDetail(item)"
        >
          <div>Date: {{ item.businessDate || '—' }}</div>
          <div>Location: {{ item.locationName || '—' }}</div>
        </div>
        <van-empty v-if="!loading && auditData.list.length === 0" description="No data" />
      </van-list>
    </van-pull-refresh>

    <van-action-sheet
      v-model="showAuditSheet"
      :title="null"
      class="audit-action-sheet"
      :round="false"
      :closeable="true"
    >
      <div class="action-sheet-close" @click="showAuditSheet = false">
        <van-icon name="close" size="20" color="#2a4062" />
      </div>
      <div class="action-sheet-content">
        <div class="sheet-title">Audit List</div>
        <div v-for="(item, index) in auditList" :key="item.id || index" class="audit-card">
          <div class="audit-row">
            <span class="audit-label">Report Items:</span>
            <span class="audit-value">{{ item.auditor || '—' }}</span>
          </div>
          <div class="audit-row">
            <span class="audit-label">Shift:</span>
            <span class="audit-value">{{ item.shiftName || '—' }}</span>
          </div>
          <!-- <div class="audit-row">
            <span class="audit-label">Inspector:</span>
            <van-tag
              v-for="ins in item.inspectorList"
              :key="ins.id"
              plain
              type="primary"
              class="audit-tag"
            >{{ ins.inspectorName }}</van-tag>
            <span v-if="!item.inspectorList || !item.inspectorList.length" class="audit-value">—</span>
          </div> -->
          <div class="audit-row audit-row--block">
            <span class="audit-label">Report Items:</span>
            <div v-if="item.auditDetailList && item.auditDetailList.length" class="report-item-list">
              <div
                v-for="detail in item.auditDetailList"
                :key="detail.id"
                class="report-item"
              >
                <van-checkbox
                  v-model="detail._checked"
                  disabled
                  shape="square"
                  icon-size="18px"
                  checked-color="#2a4062"
                  class="report-item__checkbox"
                />
                <span class="report-item__text">{{ detail.value }}</span>
              </div>
            </div>
            <span v-else class="audit-value">—</span>
          </div>
          <div class="audit-row">
            <span class="audit-label">Pictures:</span>
          </div>
          <div class="photo-grid">
            <van-image
              v-for="url in item.pictureUrls"
              :key="url"
              width="100%"
              height="90"
              fit="cover"
              :src="url"
              @click="previewImage(item.pictureUrls, url)"
            />
          </div>
        </div>
        <van-empty v-if="!auditListLoading && auditList.length === 0" description="No data" />
      </div>
    </van-action-sheet>

    <van-image-preview v-model="showPreview" :images="previewImages" :start-position="previewIndex" />
  </div>
</template>

<script>
import objstorage from "@/common/localstorage/storage.js";
import objaxios from "axios";
import objurl from "@/common/interface/url.js";

const OSS_BASE = "https://hqsautomotive.oss-us-west-1.aliyuncs.com/";

export default {
  name: "AuditReportMobile",
  data() {
    const end = new Date();
    const start = new Date();
    start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
    return {
      sortData: {},
      showCalendar: false,
      minDate: new Date(2020, 0, 1),
      maxDate: new Date(2030, 11, 31),
      defaultDateRange: [start, end],
      dateRange: [start, end],
      dateRangeText: "",
      queryParams: { pageNum: 1, pageSize: 10, sortId: "" },
      auditData: {
        totalReport: 0,
        totalHc: 0,
        taskCompletionRate: "0%",
        list: [],
        total: 0,
      },
      loading: false,
      finished: false,
      refreshing: false,
      showAuditSheet: false,
      auditList: [],
      auditListLoading: false,
      showPreview: false,
      previewImages: [],
      previewIndex: 0,
    };
  },
  created() {
    this.parseSortData();
    this.syncDateText();
    this.loadAudit();
  },
  methods: {
    parseSortData() {
      try {
        this.sortData = JSON.parse(this.$route.query.sortData || "{}");
        this.queryParams.sortId = String(this.sortData.id || "");
      } catch (e) {
        this.$toast.fail("Data parsing error");
        this.$router.back();
      }
    },
    formatDate(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },
    syncDateText() {
      this.dateRangeText = this.formatDateRangeText(this.dateRange[0], this.dateRange[1]);
    },
    formatDateRangeText(start, end) {
      const format = (date) => `${date.getMonth() + 1}/${date.getDate()}`;
      return `${format(start)} - ${format(end)}`;
    },
    calendarFormatter(day) {
      if (day.type === "start") day.bottomInfo = "Start";
      else if (day.type === "end") day.bottomInfo = "End";
      else if (day.type === "start-end") day.bottomInfo = "Start/End";
      return day;
    },
    getDateParams() {
      return {
        startBusinessDate: this.formatDate(this.dateRange[0]),
        endBusinessDate: this.formatDate(this.dateRange[1]),
        sortId: this.queryParams.sortId,
      };
    },
    setAuth() {
      const user = objstorage.get("userinfo") || {};
      objaxios.defaults.headers.common["Authorization"] = (user.tokenHead || "") + (user.token || "");
    },
    loadAudit() {
      if (!this.queryParams.sortId) return;
      this.setAuth();
      const dateParams = this.getDateParams();
      objaxios.post(objurl.audit_statistics_mobile, dateParams).then((res) => {
        if (res.data.code === "200" || res.data.code === 200) {
          const data = res.data.data || {};
          this.auditData.totalReport = data.totalReport || 0;
          this.auditData.totalHc = data.totalHc || 0;
          this.auditData.taskCompletionRate = data.taskCompletionRate || "0%";
        }
      });
      objaxios
        .post(objurl.audit_statistics_page_mobile, {
          ...dateParams,
          pageNum: this.queryParams.pageNum,
          pageSize: this.queryParams.pageSize,
        })
        .then((res) => {
          this.loading = false;
          this.refreshing = false;
          if (res.data.code === "200" || res.data.code === 200) {
            const data = res.data.data || {};
            const list = data.list || [];
            this.auditData.list =
              this.queryParams.pageNum === 1 ? list : this.auditData.list.concat(list);
            this.auditData.total = Number(data.total) || 0;
            this.finished = this.auditData.list.length >= this.auditData.total;
          } else {
            this.$toast.fail(res.data.message || "Failed to get data");
          }
        })
        .catch(() => {
          this.loading = false;
          this.refreshing = false;
        });
    },
    onDateConfirm(dates) {
      if (dates && dates.length === 2) {
        this.dateRange = dates;
        this.showCalendar = false;
        this.syncDateText();
      } else {
        this.$toast("Please select a date range");
      }
    },
    handleSearch() {
      this.queryParams.pageNum = 1;
      this.finished = false;
      this.auditData.list = [];
      this.loadAudit();
    },
    onRefresh() {
      this.queryParams.pageNum = 1;
      this.finished = false;
      this.auditData.list = [];
      this.loadAudit();
    },
    onLoad() {
      if (this.auditData.list.length >= this.auditData.total) {
        this.finished = true;
        this.loading = false;
        return;
      }
      this.queryParams.pageNum += 1;
      this.loadAudit();
    },
    goDetail(item) {
      this.showAuditSheet = true;
      this.auditList = [];
      this.auditListLoading = true;
      this.setAuth();
      objaxios
        .post(objurl.audit_list_mobile, {
          businessDate: item.businessDate,
          locationId: String(item.locationId),
          sortId: this.queryParams.sortId,
        })
        .then((res) => {
          this.auditListLoading = false;
          if (res.data.code === "200" || res.data.code === 200 || res.data.code === 0 || res.data.code === "0") {
            const data = Array.isArray(res.data.data) ? res.data.data : [];
            this.auditList = data.map((row) => ({
              ...row,
              auditDetailList: (row.auditDetailList || []).map((d) => ({
                ...d,
                _checked: this.isDetailYes(d),
              })),
              pictureUrls: (row.pictureList || [])
                .map((p) => p && p.picture)
                .filter(Boolean)
                .map((p) => OSS_BASE + p),
            }));
          } else {
            this.$toast.fail(res.data.message || "Failed to get data");
          }
        })
        .catch(() => {
          this.auditListLoading = false;
          this.$toast.fail("Network error");
        });
    },
    isDetailYes(detail) {
      if (!detail) return false;
      const completion = detail.completion;
      if (completion && typeof completion === "object") {
        const name = String(completion.name || "").trim().toUpperCase();
        if (name === "YES" || name === "NO") return name === "YES";
        return completion.value === 1 || completion.value === "1";
      }
      if (detail.completionValue === 1 || detail.completionValue === "1") return true;
      if (detail.completionValue === 0 || detail.completionValue === "0") return false;
      return String(completion || "").trim().toUpperCase() === "YES";
    },
    previewImage(images, url) {
      this.previewImages = images;
      this.previewIndex = images.indexOf(url);
      this.showPreview = true;
    },
    handleGoBack() {
      this.$router.go(-1);
    },
  },
};
</script>

<style lang="stylus" scoped>
.audit-report-page {
  min-height: 100vh;
  background: #f5f7fa;
}

.detail-nav-bar {
  background-color: #2a4062;
  color: #fff;
  height: 56px;
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  box-sizing: border-box;

  /deep/ .van-nav-bar__content {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    background-color: #2a4062;
  }

  /deep/ .van-nav-bar__title {
    color: #fff;
    font-weight: 500;
    font-size: 18px;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    text-align: center;
    margin: 0;
    line-height: 56px;
  }

  /deep/ .van-nav-bar__left {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex: 1;
    min-width: 24px;
    padding: 0;
  }

  /deep/ .van-nav-bar__text,
  /deep/ .van-nav-bar__arrow {
    color: #fff;
  }
}

.date-search-container {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background-color: #fff;
  margin: 10px 16px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  gap: 10px;
}

.date-input {
  flex: 1;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: #f5f7fa;
  border-radius: 6px;
  font-size: 14px;
  color: #303133;
  cursor: pointer;
  min-height: 36px;
}

.date-icon {
  color: #909399;
  font-size: 16px;
  margin-right: 8px;
}

.date-text {
  flex: 1;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.search-btn {
  background-color: #2a4062;
  color: #fff;
  min-width: 80px;
}

.calendar-popup {
  /deep/ .van-calendar__header {
    background-color: #2a4062;
    color: #fff;
  }

  /deep/ .van-calendar__day--start,
  /deep/ .van-calendar__day--end,
  /deep/ .van-calendar__day--start-end {
    background-color: #2a4062;
    color: #fff;
  }

  /deep/ .van-calendar__day--in-range {
    background-color: rgba(42, 64, 98, 0.1);
  }
}

.stats-container {
  display: flex;
  gap: 8px;
  padding: 0 16px 12px;
}

.stat-item {
  flex: 1;
  background: #fff;
  border-radius: 8px;
  padding: 12px 8px;
  text-align: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.stat-value {
  font-size: 20px;
  font-weight: 700;
  color: #2a4062;
}

.stat-label {
  margin-top: 4px;
  font-size: 11px;
  color: #909399;
  line-height: 1.3;
}

.section-title {
  padding: 0 16px 8px;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.detail-card {
  margin: 0 16px 10px;
  padding: 14px 16px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  font-size: 14px;
  color: #303133;
  line-height: 1.8;
}

.audit-action-sheet {
  /deep/ .van-action-sheet__header {
    display: none;
  }

  /deep/ .van-action-sheet__content {
    padding-top: 40px !important;
  }
}

.action-sheet-close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 100;
  cursor: pointer;
  padding: 8px;
}

.action-sheet-content {
  max-height: 75vh;
  overflow-y: auto;
  padding: 20px 16px;
  background-color: #fff;
}

.sheet-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 14px;
}

.audit-card {
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebedf0;
}

.audit-row {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 10px;
  line-height: 24px;
}

.audit-label {
  font-size: 15px;
  font-weight: 700;
  color: #2a4062;
  margin-right: 6px;
  flex-shrink: 0;
}

.audit-value {
  font-size: 14px;
  color: #606266;
}

.audit-row--block {
  flex-direction: column;
  align-items: flex-start;
  flex-wrap: nowrap;
}

.report-item-list {
  width: 100%;
  margin-top: 4px;
}

.report-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  line-height: 24px;

  &:last-child {
    margin-bottom: 0;
  }
}

.report-item__checkbox {
  margin-right: 10px;
  flex-shrink: 0;
  pointer-events: none;

  /deep/ .van-checkbox__icon {
    border-radius: 3px;
    border-color: #b0b8c4;
  }

  /deep/ .van-checkbox__icon--checked {
    border-color: #2a4062;
    background-color: #fff;
  }

  /deep/ .van-checkbox__icon--checked .van-icon {
    color: #2a4062;
  }
}

.report-item__text {
  font-size: 14px;
  color: #8a96a8;
}

.audit-tag {
  margin: 0 6px 0 0;
}

.photo-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}
</style>



// WEBPACK FOOTER //
// src/views/AuditReportMobile.vue